import React, { useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/auth/AuthProvider";
import { ConversationRecorder } from "@/services/conversation/ConversationRecorder";
import { Agent } from "@/types/agent/agent";
import { ConversationDetails } from "@/types/conversation/conversation";
import { formatDate, formatDuration, formatFieldName } from "@/utils/formatting/general";
import { ConversationAnalysisExpanded } from "./ConversationAnalysisExpanded";

interface ConversationAnalysisListProps {
  conversations: ConversationDetails[];
  isLoading: boolean;
  agents: Agent[];
  invalidateConversationQueries: () => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAllVisible: () => void;
  allVisibleSelected: boolean;
}

const TopicBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="relative text-xs px-2 py-1 rounded-full inline-block w-fit font-medium text-gray-700 bg-white">
    <span
      className="absolute inset-0 rounded-full p-[1px]"
      style={{ background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)" }}
    >
      <span className="block w-full h-full rounded-full bg-white" />
    </span>
    <span className="relative">{children}</span>
  </span>
);

export const ConversationAnalysisList = ({
  conversations,
  isLoading,
  agents,
  invalidateConversationQueries,
  selectedIds,
  onToggleSelect,
  onToggleSelectAllVisible,
  allVisibleSelected,
}: ConversationAnalysisListProps) => {
  const { user } = useAuth();
  const [expandedConversation, setExpandedConversation] = useState<string | null>(null);
  const [analyzingConversation, setAnalyzingConversation] = useState<string | null>(null);

  const isTeamAgent = (agent: Agent | undefined) => agent && agent.user_id !== user?.id;

  const handleToggleExpansion = (conversationId: string) => {
    setExpandedConversation(expandedConversation === conversationId ? null : conversationId);
  };

  const handleRunAnalysis = async (conversation: ConversationDetails) => {
    setAnalyzingConversation(conversation.elevenlabs_conversation_id || "");

    try {
      toast.info("Starting conversation analysis...");

      const agent = agents.find((a) => a.id === conversation.agent_id);
      if (!agent) {
        toast.error("Agent not found for this conversation");
        return;
      }

      const success = await ConversationRecorder.updateConversationDetailsWithTranscript(
        conversation.elevenlabs_conversation_id,
        agent,
      );

      if (success) {
        toast.success("Conversation analysis completed successfully");
        invalidateConversationQueries();
      } else {
        toast.error("Failed to complete conversation analysis");
      }
    } catch (error) {
      console.error("Analysis failed:", error);
      toast.error("Failed to analyze conversation");
    } finally {
      setAnalyzingConversation(null);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600">Loading conversation analysis...</p>
        </div>
      </Card>
    );
  }

  if (conversations.length === 0) {
    return null;
  }

  return (
    <Card className="overflow-hidden">
      {/* Mobile Card View */}
      <div className="block md:hidden">
        <div className="space-y-4 p-4">
          {conversations.map((conversation) => {
            const summary = conversation.summary || conversation.analysis_result?.summary || "";
            const topics = conversation.topics || conversation.analysis_result?.topics || [];
            const leadInfo =
              conversation.lead_info || conversation.analysis_result?.lead_info || {};
            const leadInfoCount = Object.keys(leadInfo).length;
            const agent = agents.find((a) => a.id === conversation.agent_id);

            const convoId = conversation.elevenlabs_conversation_id || "";
            const isSelected = !!convoId && selectedIds.has(convoId);

            return (
              <Card
                key={conversation.id}
                className={`p-4 border-l-4 transition-colors ${
                  isSelected
                    ? "border-genie-primary bg-genie-primary/5"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    {convoId && (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleSelect(convoId)}
                        aria-label="Select conversation"
                        className="mt-1 flex-shrink-0"
                      />
                    )}
                    <div className="flex-shrink-0">
                      <GenieAvatar
                        size={40}
                        color={conversation.genie_branding?.primaryColor || "#4E9CFF"}
                        secondaryColor={conversation.genie_branding?.secondaryColor}
                        customAvatarUrl={
                          conversation.genie_branding?.logoUrl ||
                          conversation.genie_branding?.customAvatarUrl
                        }
                        gradientEnabled={conversation.genie_branding?.gradientEnabled}
                        gradientAngle={conversation.genie_branding?.gradientAngle}
                        avatarBackgroundOpacity={conversation.genie_branding?.avatarBackgroundOpacity}
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 text-sm">
                        {conversation.genie_name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>
                          {conversation.conversation_date
                            ? formatDate(conversation.conversation_date)
                            : "Unknown date"}
                        </span>
                        <span>•</span>
                        <span>
                          {conversation.duration_seconds
                            ? formatDuration(conversation.duration_seconds)
                            : "Unknown duration"}
                        </span>
                      </div>
                      {isTeamAgent(agent) && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium text-gray-700 bg-gray-200 shadow-sm mt-1">
                          Team
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        handleToggleExpansion(conversation.elevenlabs_conversation_id || "")
                      }
                      className="px-2"
                    >
                      {expandedConversation === conversation.elevenlabs_conversation_id ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                    {!conversation.has_analysis && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRunAnalysis(conversation)}
                        disabled={analyzingConversation === conversation.elevenlabs_conversation_id}
                        className="px-2"
                      >
                        {analyzingConversation === conversation.elevenlabs_conversation_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {summary && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Summary</p>
                    <p className="text-sm text-gray-700 line-clamp-2">{summary}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 text-xs">
                  {topics.length > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Topics:</span>
                      <span className="text-gray-700">{topics.length}</span>
                    </div>
                  )}
                  {leadInfoCount > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Lead Info:</span>
                      <span className="text-gray-700">{leadInfoCount} fields</span>
                    </div>
                  )}
                </div>

                {/* Mobile Expanded Content */}
                {expandedConversation === conversation.elevenlabs_conversation_id && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <ConversationAnalysisExpanded conversation={conversation} />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-[44px] px-3 lg:px-4 py-3">
                <Checkbox
                  checked={allVisibleSelected}
                  onCheckedChange={onToggleSelectAllVisible}
                  aria-label="Select all visible conversations"
                  disabled={conversations.length === 0}
                />
              </th>
              <th className="w-[280px] px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Conversation
              </th>
              <th className="w-[300px] px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Summary
              </th>
              <th className="w-[240px] px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Topics
              </th>
              <th className="w-[180px] px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lead Info
              </th>
              <th className="w-[100px] px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {conversations.map((conversation) => {
              const summary = conversation.summary || conversation.analysis_result?.summary || "";
              const topics = conversation.topics || conversation.analysis_result?.topics || [];
              const leadInfo =
                conversation.lead_info || conversation.analysis_result?.lead_info || {};
              const leadInfoCount = Object.keys(leadInfo).length;
              const agent = agents.find((a) => a.id === conversation.agent_id);

              const convoId = conversation.elevenlabs_conversation_id || "";
              const isSelected = !!convoId && selectedIds.has(convoId);

              return (
                <React.Fragment key={conversation.id}>
                  <tr
                    className={`hover:bg-gray-50 ${
                      isSelected ? "bg-genie-primary/5" : ""
                    }`}
                  >
                    {/* Selection */}
                    <td className="px-3 lg:px-4 py-4 align-top">
                      {convoId && (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => onToggleSelect(convoId)}
                          aria-label="Select conversation"
                          className="mt-1"
                        />
                      )}
                    </td>

                    {/* Conversation Info */}
                    <td className="px-4 lg:px-6 py-4 align-top">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <GenieAvatar
                            size={48}
                            color={conversation.genie_branding?.primaryColor || "#4E9CFF"}
                            secondaryColor={conversation.genie_branding?.secondaryColor}
                            customAvatarUrl={
                              conversation.genie_branding?.logoUrl ||
                              conversation.genie_branding?.customAvatarUrl
                            }
                            gradientEnabled={conversation.genie_branding?.gradientEnabled}
                            gradientAngle={conversation.genie_branding?.gradientAngle}
                            avatarBackgroundOpacity={conversation.genie_branding?.avatarBackgroundOpacity}
                          />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {conversation.genie_name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {conversation.conversation_date
                              ? formatDate(conversation.conversation_date)
                              : "Unknown date"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {conversation.duration_seconds
                              ? formatDuration(conversation.duration_seconds)
                              : "Unknown duration"}
                          </div>
                          {isTeamAgent(agent) && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium text-gray-700 bg-gray-200 shadow-sm mt-1">
                              Team
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Summary */}
                    <td className="px-4 lg:px-6 py-4 align-top">
                      <div className="max-w-[280px]">
                        {summary ? (
                          <p className="text-sm text-gray-700 line-clamp-3">{summary}</p>
                        ) : (
                          <span className="text-sm text-gray-400 italic">No summary available</span>
                        )}
                      </div>
                    </td>

                    {/* Topics */}
                    <td className="px-4 lg:px-6 py-4 align-top">
                      {topics.length > 0 ? (
                        <div className="flex flex-col gap-1 lg:gap-2">
                          {topics.slice(0, 3).map((topic: string, index: number) => (
                            <TopicBadge key={index}>{topic}</TopicBadge>
                          ))}
                          {topics.length > 3 && (
                            <span className="text-xs text-gray-500">+{topics.length - 3} more</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No topics</span>
                      )}
                    </td>

                    {/* Lead Info */}
                    <td className="px-4 lg:px-6 py-4 align-top">
                      <div className="max-w-[160px]">
                        {leadInfoCount > 0 ? (
                          <div className="flex flex-col gap-1 lg:gap-2">
                            {Object.entries(leadInfo)
                              .slice(0, 2)
                              .map(([key, value]) => (
                                <div key={key} className="flex flex-col min-w-0">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate">
                                    {formatFieldName(key)}
                                  </span>
                                  <span
                                    className="text-sm text-gray-900 truncate"
                                    title={String(value)}
                                  >
                                    {String(value) || "-"}
                                  </span>
                                </div>
                              ))}
                            {leadInfoCount > 2 && (
                              <span className="text-xs text-gray-500">
                                +{leadInfoCount - 2} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No lead info</span>
                        )}
                      </div>
                    </td>

                    {/* Details */}
                    <td className="px-4 lg:px-6 py-4 align-top">
                      <div className="flex flex-col lg:flex-row gap-1 lg:gap-2 min-w-[80px]">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            handleToggleExpansion(conversation.elevenlabs_conversation_id || "")
                          }
                          className="px-2"
                        >
                          {expandedConversation === conversation.elevenlabs_conversation_id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                        {!conversation.has_analysis && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRunAnalysis(conversation)}
                            disabled={
                              analyzingConversation === conversation.elevenlabs_conversation_id
                            }
                            className="px-2"
                          >
                            {analyzingConversation === conversation.elevenlabs_conversation_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded row */}
                  {expandedConversation === conversation.elevenlabs_conversation_id && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <ConversationAnalysisExpanded conversation={conversation} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
